import { useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useNavIndicators } from "@/features/indicators/hooks/use-nav-indicators";
import { useAuth } from "@/shared/auth/auth.context";
import { canDo, Permission } from "@/shared/auth/permissions";
import { sidebarHref } from "@/shared/components/layout/module-landing";
import {
  isNavItemActive,
  isSidebarVisible,
  withRolledIndicators,
} from "@/shared/components/layout/nav-path";
import { usePayrollNavSetupFirst } from "@/shared/components/layout/payroll-nav-order";
import { isPayrollAdminPath } from "@/shared/components/layout/payroll-sidebar-model";
import { PayrollSidebarNav } from "@/shared/components/layout/payroll-sidebar-nav";
import {
  buildNavIndicatorOverrides,
  EMPTY_NAV_INDICATORS,
  NAV_SECTIONS,
  type NavItem,
  type NavSection,
} from "@/shared/components/layout/sidebar-nav.config";
import { keepItem, lockedClickHandler } from "@/shared/components/layout/sidebar-nav.gate";
import { Item } from "@/shared/components/layout/sidebar-nav-item";
import { SidebarNavTransition } from "@/shared/components/layout/sidebar-nav-transition";
import { UpgradeDialog } from "@/shared/entitlements/components/upgrade-dialog";
import type { FeatureKey } from "@/shared/entitlements/entitlements.constants";
import { useEntitlements } from "@/shared/entitlements/entitlements.context";
import { resolveFeatureConfirmed } from "@/shared/entitlements/hooks/use-feature-enabled";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { useSidebarCollapsed } from "@/shared/hooks/use-sidebar-collapsed";
import { cn } from "@/shared/lib/cn";

export {
  buildNavIndicatorOverrides,
  EMPTY_NAV_INDICATORS,
  EMPTY_NAV_OVERRIDES,
} from "@/shared/components/layout/sidebar-nav.config";

export function SidebarNav() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { workspace } = useActiveWorkspace();
  return (
    <SidebarNavTransition
      key={workspace.id}
      scope={isPayrollAdminPath(pathname, workspace.slug) ? "payroll" : "workspace"}
      workspace={<WorkspaceSidebarNav />}
      payroll={<PayrollSidebarNav />}
    />
  );
}

function WorkspaceSidebarNav() {
  const { location } = useRouterState();
  const { collapsed } = useSidebarCollapsed();
  const { workspace } = useActiveWorkspace();
  const auth = useAuth();
  const { features } = useEntitlements();
  const pathname = location.pathname;
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<FeatureKey | null>(null);

  function openUpgradeDialog(feature: FeatureKey): void {
    setUpgradeFeature(feature);
    setUpgradeOpen(true);
  }

  // reason: indicator polling stays local to the sidebar because this is the
  // only consumer today, but the payload is now generic and fan-out ready.
  const indicatorsQuery = useNavIndicators(workspace.id);
  const navIndicators = indicatorsQuery.data ?? EMPTY_NAV_INDICATORS;

  const accountRole = auth.user?.accountRole ?? null;
  const workspaceRoles = auth.memberships
    .filter((m) => m.workspaceId === workspace.id)
    .map((m) => m.role);
  const setupFirst = usePayrollNavSetupFirst(
    workspace.id,
    canDo(Permission.PAYROLL_CONFIG_VIEW, { accountRole, workspaceRoles }) &&
      resolveFeatureConfirmed(features, "payroll"),
  );

  const sections = useMemo(() => {
    const dynamicOverrides = buildNavIndicatorOverrides(navIndicators);
    return NAV_SECTIONS.map((s) => ({
      ...s,
      items: s.items
        .map((item) => keepItem(item, accountRole, workspaceRoles, features, dynamicOverrides))
        .filter((i): i is NavItem => i !== null)
        .map(withRolledIndicators)
        .filter(isSidebarVisible),
    })).filter((s) => s.items.length > 0);
  }, [accountRole, workspaceRoles, navIndicators, features]);

  return (
    <>
      <nav
        aria-label="Primary"
        className={cn(
          "flex flex-col gap-4 overflow-x-hidden overflow-y-auto pb-4 pt-1",
          collapsed ? "px-2" : "px-3",
          "[scrollbar-width:thin] [scrollbar-color:var(--border-base)_transparent]",
        )}
      >
        {sections.map((section) => (
          <Section
            key={section.title !== "" ? section.title : section.divider ? "later" : "week"}
            section={section}
            pathname={pathname}
            workspaceSlug={workspace.slug}
            workspaceId={workspace.id}
            collapsed={collapsed}
            setupFirst={setupFirst}
            onLockedClick={openUpgradeDialog}
          />
        ))}
      </nav>
      <UpgradeDialog open={upgradeOpen} feature={upgradeFeature} onOpenChange={setUpgradeOpen} />
    </>
  );
}

function Section({
  section,
  pathname,
  workspaceSlug,
  workspaceId,
  collapsed,
  setupFirst,
  onLockedClick,
}: {
  section: NavSection;
  pathname: string;
  workspaceSlug: string;
  workspaceId: string;
  collapsed: boolean;
  setupFirst: boolean;
  onLockedClick: (feature: FeatureKey) => void;
}) {
  return (
    <section
      className={cn(
        "group flex flex-col gap-px",
        section.divider && !collapsed && "border-t border-border pt-3",
      )}
    >
      {collapsed || section.title === "" ? null : (
        <div className="flex items-center justify-between px-2.5 pb-1.5 pt-2.5">
          <span className="font-mono text-3xs font-semibold uppercase tracking-wider text-text-quaternary">
            {section.title}
          </span>
        </div>
      )}
      {section.items.map((item) => (
        <Item
          key={item.label}
          item={item}
          resolvedHref={sidebarHref(item, workspaceSlug, workspaceId, setupFirst)}
          isActive={isNavItemActive(pathname, item, workspaceSlug)}
          collapsed={collapsed}
          onLockedClick={lockedClickHandler(item, onLockedClick)}
        />
      ))}
    </section>
  );
}
